import { CSSProperties, useState } from 'react';
import { CategoryBadge } from './CategoryBadge';
import { StarRating } from './StarRating';
import { gradientFor } from './imageGradients';

/**
 * StopCard — horizontal itinerary item with a thumbnail (photo or gradient).
 * Used for stops within a day of a trip. Serif title + sans tip + meta row.
 * Hover lifts and warms the hairline to gold.
 *
 * Thumbnail: pass `photoUrl` for a real image, or `gradientKey` (food, drink,
 * view, nature, landmark, art, shop, travel) to pull a base colour + icon from
 * the canonical palette. `gradient`/`icon` override the resolved values.
 */
export interface StopCardProps {
  title: string;
  /** Short tip/description under the title. */
  tip?: string;
  /** Alias of `tip` (repo call-site compat) — used when `tip` is omitted. */
  description?: string;
  /** Inline category pill, e.g. "Gardens". */
  category?: string;
  /** Location name, e.g. "Sintra". */
  location?: string;
  /** Palette key for the thumbnail fallback: food | drink | view | nature | landmark | art | shop | travel. Default 'nature'. */
  gradientKey?: 'food' | 'drink' | 'view' | 'nature' | 'landmark' | 'art' | 'shop' | 'travel';
  /** Custom CSS background overriding the palette base colour. */
  gradient?: string;
  /** Tabler icon name (no `ti-` prefix) overriding the palette icon. */
  icon?: string;
  /** Photo URL — replaces the gradient when present. */
  photoUrl?: string | null;
  /** Optional focal point (0–100) for object-position when a photo is shown. */
  photoFocal?: { x: number; y: number } | null;
  /** The traveler's own rating (1–5): shows a small read-only ★★★★☆ under the text. */
  ownRating?: number;
  onClick?: () => void;
  style?: CSSProperties;
}

export function StopCard({
  title, tip, description, category, location,
  gradientKey = 'nature', gradient, icon, photoUrl, photoFocal, onClick, style, ownRating,
}: StopCardProps) {
  const [hover, setHover] = useState(false);
  const g = gradientFor(gradientKey);
  const bg = gradient || g.base;
  const glyph = icon || g.icon;
  const bodyText = tip ?? description;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', gap: 14,
        border: `1px solid ${hover ? 'var(--accent-border)' : 'var(--line)'}`,
        boxShadow: hover ? 'var(--shadow-hover)' : 'none',
        borderRadius: 'var(--r-card)', padding: 'var(--sp-stop-pad)',
        cursor: onClick ? 'pointer' : 'default',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'border-color .3s cubic-bezier(.16,.8,.3,1), transform .3s cubic-bezier(.16,.8,.3,1)',
        background: 'var(--bg)',
        ...style,
      }}
    >
      <div style={{ position: 'relative', width: 108, minHeight: 92, flexShrink: 0, borderRadius: 8, overflow: 'hidden' }}>
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={title}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
              objectPosition: photoFocal ? `${photoFocal.x}% ${photoFocal.y}%` : undefined,
              transform: hover ? 'scale(1.08)' : 'scale(1)', transition: 'transform .5s cubic-bezier(.16,.8,.3,1)',
            }}
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, background: bg, transform: hover ? 'scale(1.08)' : 'scale(1)', transition: 'transform .5s cubic-bezier(.16,.8,.3,1)' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(120% 100% at 30% 20%, rgba(255,255,255,.10), transparent 60%)' }} />
            <i className={`ti ti-${glyph}`} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 'calc(22px * var(--text-scale))', color: 'rgb(var(--cream) / .26)' }} />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ lineHeight: 'var(--lh-card-title)' }}>
          <span style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 'var(--fs-card-title-sm)', color: 'var(--text)', verticalAlign: 'middle' }}>{title}</span>
          {category ? <span style={{ marginLeft: 8 }}><CategoryBadge label={category} /></span> : null}
        </div>
        {bodyText ? (
          <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-soft)', fontSize: 'var(--fs-footnote)', lineHeight: 1.62, marginTop: 6, textWrap: 'pretty' }}>{bodyText}</p>
        ) : null}
        {ownRating ? <StarRating value={ownRating} style={{ marginTop: 8 }} /> : null}
        {location ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 9, color: 'var(--text-dim)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-meta)' }}>
            {location ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><i className="ti ti-map-pin" style={{ fontSize: 'calc(12px * var(--text-scale))' }} />{location}</span> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default StopCard;
